const TOKEN_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';
const SAAS_TOKEN_KEY = 'saasAccessToken';
const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/+$/, '') ?? '';

// O backend expõe as rotas sob o prefixo /api (setGlobalPrefix('api')).
// Sem VITE_API_URL (dev), as chamadas ficam relativas e o proxy do Vite resolve.
function buildApiUrl(path) {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return API_BASE_URL ? `${API_BASE_URL}/api${suffix}` : `/api${suffix}`;
}

function buildAbsoluteUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return API_BASE_URL ? `${API_BASE_URL}${suffix}` : suffix;
}

export const authStore = {
  get access() {
    return localStorage.getItem(TOKEN_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access, refresh) {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const saasAuth = {
  get access() {
    return localStorage.getItem(SAAS_TOKEN_KEY);
  },
  set(token) {
    localStorage.setItem(SAAS_TOKEN_KEY, token);
  },
  clear() {
    localStorage.removeItem(SAAS_TOKEN_KEY);
  },
};

async function authorizedFetch(url, options = {}, token = authStore.access) {
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(buildAbsoluteUrl(url), { ...options, headers });

  if (res.status === 401 && authStore.refresh && token === authStore.access && !String(url).includes('/auth/refresh')) {
    const refreshed = await tryRefresh();
    if (refreshed) return authorizedFetch(url, options, authStore.access);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = body?.message || res.statusText;
    const error = new Error(Array.isArray(msg) ? msg.join(', ') : msg);
    error.status = res.status;
    throw error;
  }

  return res;
}

async function request(path, options = {}, token = authStore.access) {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = { ...(isFormData ? {} : { 'Content-Type': 'application/json' }), ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(buildApiUrl(path), { ...options, headers });

  if (res.status === 401 && authStore.refresh && token === authStore.access && !path.startsWith('/auth/refresh')) {
    const refreshed = await tryRefresh();
    if (refreshed) return request(path, options, authStore.access);
  }

  const body = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const msg = body?.message || res.statusText;
    const error = new Error(Array.isArray(msg) ? msg.join(', ') : msg);
    error.status = res.status;
    error.code = body?.code;
    throw error;
  }
  return body;
}

export async function fetchProtectedAssetBlobUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const response = await authorizedFetch(path);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export async function openProtectedAsset(path) {
  const openedWindow = window.open('', '_blank', 'noopener,noreferrer');
  try {
    const blobUrl = await fetchProtectedAssetBlobUrl(path);
    if (openedWindow) {
      openedWindow.location.href = blobUrl;
      openedWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
    } else {
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    if (openedWindow) openedWindow.close();
    throw error;
  }
}

let refreshPromise = null;

async function tryRefresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(buildApiUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: authStore.refresh }),
      });
      if (!res.ok) throw new Error('refresh failed');
      const body = await res.json();
      authStore.set(body.accessToken, body.refreshToken);
      return true;
    } catch {
      authStore.clear();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export const api = {
  register: (data) => request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data) => request('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  initialPlanCheckout: (data) => request('/auth/initial-plan/checkout', { method: 'POST', body: JSON.stringify(data) }),
  listPublicPlans: () => request('/saas/plans/public', {}, null),
  logout: () => request('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken: authStore.refresh }) }),
  me: () => request('/users/me'),
  listUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  roles: () => request('/roles'),
  dashboard: () => request('/dashboard'),
  listLabelFormats: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/label-formats${qs ? `?${qs}` : ''}`);
  },
  listModels: () => request('/models'),
  applyModel: (id, data = {}) => request(`/models/${id}/apply`, { method: 'POST', body: JSON.stringify(data) }),
  getLabelFormat: (id) => request(`/label-formats/${id}`),
  createLabelFormat: (data) => request('/label-formats', { method: 'POST', body: JSON.stringify(data) }),
  updateLabelFormat: (id, data) => request(`/label-formats/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLabelFormat: (id) => request(`/label-formats/${id}`, { method: 'DELETE' }),
  listTemplates: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/templates${qs ? `?${qs}` : ''}`);
  },
  getTemplate: (id, options = {}) => {
    const headers = options.password ? { 'x-label-password': options.password } : undefined;
    return request(`/templates/${id}`, { headers });
  },
  createTemplate: (data) => request('/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (id, data) => request(`/templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTemplate: (id) => request(`/templates/${id}`, { method: 'DELETE' }),
  listPrintJobs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/print-jobs${qs ? `?${qs}` : ''}`);
  },
  getPrintJob: (id) => request(`/print-jobs/${id}`),
  createPrintJob: (data) => request('/print-jobs', { method: 'POST', body: JSON.stringify(data) }),
  updatePrintJob: (id, data) => request(`/print-jobs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePrintJob: (id) => request(`/print-jobs/${id}`, { method: 'DELETE' }),
  clearPrintJobs: () => request('/print-jobs', { method: 'DELETE' }),
  generateCode: (data) => request('/print-engine/code', { method: 'POST', body: JSON.stringify(data) }),
  listPrinters: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/printers${qs ? `?${qs}` : ''}`);
  },
  getPrinter: (id) => request(`/printers/${id}`),
  createPrinter: (data) => request('/printers', { method: 'POST', body: JSON.stringify(data) }),
  updatePrinter: (id, data) => request(`/printers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePrinter: (id) => request(`/printers/${id}`, { method: 'DELETE' }),
  listDataSources: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/data-sources${qs ? `?${qs}` : ''}`);
  },
  getDataSource: (id) => request(`/data-sources/${id}`),
  createDataSource: (data) => request('/data-sources', { method: 'POST', body: JSON.stringify(data) }),
  updateDataSource: (id, data) => request(`/data-sources/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteDataSource: (id) => request(`/data-sources/${id}`, { method: 'DELETE' }),
  listCounters: () => request('/counters'),
  getCounter: (id) => request(`/counters/${id}`),
  createCounter: (data) => request('/counters', { method: 'POST', body: JSON.stringify(data) }),
  updateCounter: (id, data) => request(`/counters/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCounter: (id) => request(`/counters/${id}`, { method: 'DELETE' }),
  incrementCounter: (id) => request(`/counters/${id}/increment`, { method: 'POST' }),
  listFormulas: () => request('/formulas'),
  getFormula: (id) => request(`/formulas/${id}`),
  createFormula: (data) => request('/formulas', { method: 'POST', body: JSON.stringify(data) }),
  updateFormula: (id, data) => request(`/formulas/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteFormula: (id) => request(`/formulas/${id}`, { method: 'DELETE' }),
  listBatches: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/batches${qs ? `?${qs}` : ''}`);
  },
  getBatch: (id) => request(`/batches/${id}`),
  createBatch: (data) => request('/batches', { method: 'POST', body: JSON.stringify(data) }),
  updateBatch: (id, data) => request(`/batches/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBatch: (id) => request(`/batches/${id}`, { method: 'DELETE' }),
  listCategories: () => request('/categories'),
  getCategory: (id) => request(`/categories/${id}`),
  createCategory: (data) => request('/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: (id, data) => request(`/categories/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCategory: (id) => request(`/categories/${id}`, { method: 'DELETE' }),
  listImageAssets: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return request(`/image-assets${qs ? `?${qs}` : ''}`);
  },
  uploadImageAssets: (files, options = {}) => {
    const formData = new FormData();
    Array.from(files || []).forEach((file) => formData.append('files', file));
    if (options.category) formData.append('category', options.category);
    if (options.tags?.length) formData.append('tags', JSON.stringify(options.tags));
    return request('/image-assets/upload', { method: 'POST', body: formData });
  },
  updateImageAsset: (id, data) => request(`/image-assets/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }),
  markImageAssetUsed: (id) => request(`/image-assets/${encodeURIComponent(id)}/use`, { method: 'POST' }),
  deleteImageAsset: (id) => request(`/image-assets/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const saasApi = {
  login: (data) => request('/saas/auth/login', { method: 'POST', body: JSON.stringify(data) }, null),
  me: () => request('/saas/me', {}, saasAuth.access),
  stats: () => request('/saas/stats', {}, saasAuth.access),
  listPlans: () => request('/saas/plans', {}, saasAuth.access),
  listModels: () => request('/saas/models', {}, saasAuth.access),
  createPlan: (data) => request('/saas/plans', { method: 'POST', body: JSON.stringify(data) }, saasAuth.access),
  createModel: (data) => request('/saas/models', { method: 'POST', body: JSON.stringify(data) }, saasAuth.access),
  updatePlan: (id, data) => request(`/saas/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, saasAuth.access),
  updateModel: (id, data) => request(`/saas/models/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, saasAuth.access),
  removePlan: (id) => request(`/saas/plans/${id}`, { method: 'DELETE' }, saasAuth.access),
  removeModel: (id) => request(`/saas/models/${id}`, { method: 'DELETE' }, saasAuth.access),
  listTenants: () => request('/saas/tenants', {}, saasAuth.access),
  getTenant: (id) => request(`/saas/tenants/${id}`, {}, saasAuth.access),
  createTenant: (data) => request('/saas/tenants', { method: 'POST', body: JSON.stringify(data) }, saasAuth.access),
  updateTenant: (id, data) => request(`/saas/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, saasAuth.access),
  checkout: (id, data) => request(`/saas/tenants/${id}/checkout`, { method: 'POST', body: JSON.stringify(data) }, saasAuth.access),
  listInvoices: () => request('/saas/invoices', {}, saasAuth.access),
  payInvoice: (id, data = {}) => request(`/saas/invoices/${id}/pay`, { method: 'POST', body: JSON.stringify(data) }, saasAuth.access),
};
